/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Form, FormGroup } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { loadCommon } from '../../actions/common';
import { setDate, getUTCEndOf, getUtcDateUnit, isSame } from '../../utils/date';
import { updateFn } from '../form/common';
import { loadConfigById, updateConfigById } from '../../slice/surveyConfig';
import { processSelfOnboarding } from '../../actions/api';
import { uniqueSurveyDatesBySurveyListSummary } from '../../constants/initiative';
import { BasicAlert } from '../alert/BasicAlert';
import { loadSurveyListSummaryByInitiativeId, reloadSurvey, reloadSurveyListSummary } from '../../actions/survey';
import LoadingPlaceholder from '../LoaderContainer/LoadingPlaceholder';
import { ROUTES } from '../../constants/routes';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { DashboardSection } from '../dashboard';
import { reloadSurveyList } from '../../slice/initiativeSurveyListSlice';
import { useAppSelector } from '../../reducers';
import { generateUrl } from '../../routes/util';
import { SurveyContext } from '../survey-container/SurveyContainer';
import Loader from '../loader';
import SurveyUnitsAndCurrency from './partials/SurveyUnitsAndCurrency';
import { DataPeriods } from '../../types/universalTracker';
import { SurveySettings } from './partials/SurveySettings';
import { SurveySettingsMenu } from './partials/SurveySettingsMenu';
import { SurveySettingsHeader } from './partials/SurveySettingsHeader';
import NotAuthorised from '../../routes/not-authorised';
import { blueprintDefaultUnitConfig, UnitConfig } from '../../model/surveyData';
import { SurveyType } from '../../types/survey';
import './SurveyConfiguration.scss';
import { ConfigFormData, DefaultBlueprintCode } from './types';
import { getCurrentUser, isStaff } from '../../selectors/user';
import { SurveyPermissions } from '../../services/permissions/SurveyPermissions';
import { InitiativePermissions } from '../../services/permissions/InitiativePermissions';
import { useGetDefaultSurveyConfigQuery } from '../../api/default-survey-config';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { hasSurveyConflict } from '../../utils/survey';
import { NoteInstructionContainer } from '../../features/note-instructions/NoteInstructionContainer';
import SurveyDeadlineReminder from './partials/SurveyDeadlineReminder';
import { compareScheduledDates, isValidDeadlineScheduledDates } from './utils';
import { QUESTION } from '../../constants/terminology';
import { hasEditorStateChanged } from '../../utils/lexical';

const analytics = getAnalytics();

export enum ERRORS {
  NO_ERROR = 0,
  REPORT_CONFLICT = 1,
  REPORT_LIMIT = 2,
  REPORT_INVALID = 3
};

const alertReportConflict = <div>
  <b>WARNING:</b> A Report already exists for the selected Organization, Level
  and Period. Please update your settings.
</div>;

const alertReportLimit = <div>
  <b>WARNING:</b> You have reached your limit of Report Surveys for this organization. Please contact your account manager for more information.
</div>;

const requiredFields: (keyof ConfigFormData)[] = [
  'period',
  'year',
  'month',
  'initiativeId',
];

const editRequiredFields: (keyof ConfigFormData)[] = [
  'verificationRequired',
  'evidenceRequired',
  'noteRequired',
];

const initialState: ConfigFormData = {
  verificationRequired: true,
  evidenceRequired: true,
  noteRequired: false,
  isPrivate: false,
  sourceName: DefaultBlueprintCode,
};

const isConfirmBtnDisabled = (form: ConfigFormData, isEdit: boolean) => {
  if (!isValidDeadlineScheduledDates(form)) {
    return true;
  }

  if (isEdit) {
    return editRequiredFields.some((f) => undefined === form[f] || form[f] === '');
  }

  return requiredFields.some((f) => undefined === form[f] || form[f] === '');
};

export const createEffectiveDate = (form: ConfigFormData) => {
  if (form.year && form.month !== undefined) {
    const date = setDate({
      'year': form.year,
      'month': form.month
    }, undefined, true);
    return getUTCEndOf('month', date)
  }
};

export const SurveyConfiguration = () => {
  const { surveyData, disabled } = useContext(SurveyContext);

  const dispatch = useDispatch();
  const surveyId = surveyData?._id ?? undefined;
  const history = useHistory();
  const { initiativeId = '' } = useParams<{ initiativeId: string }>();

  const [state, setState] = useState({
    isLoading: false,
    message: '',
    error: false
  });
  const { isLoading, message } = state;
  const canSetNoteInstructions = useAppSelector(FeaturePermissions.canSetNoteInstructions);

  const {
    loaded: surveyConfigLoaded,
    data: surveyConfig,
    errorMessage,
    errored,
  } = useAppSelector((state) => state.surveyConfig);

  const currentSurveyConfig = surveyId && surveyId === surveyConfig?._id ? surveyConfig : undefined;

  const [errorState, setErrorState] = useState(ERRORS.NO_ERROR);
  const [unitConfig, setUnitConfig] = useState(surveyConfig?.unitConfig);
  const [form, setForm] = useState<ConfigFormData>({
    ...initialState,
    _id: currentSurveyConfig?._id,
    noteInstructionsEditorState: currentSurveyConfig?.noteInstructionsEditorState,
    noteInstructions: currentSurveyConfig?.noteInstructions,
    initiativeId: initiativeId
  });
  const updateForm: updateFn = useCallback((update) => {
    if (update.code === 'deadlineDate' && !update.value) {
      setForm((f) => ({ ...f, deadlineDate: undefined, scheduledDates: [] }));
      return;
    }
    setForm((f) => ({ ...f, [update.code]: update.value }));
  }, []);

  const initiativeState = useAppSelector((state) => state.initiative);
  const surveyListSummaryState = useAppSelector((state) => state.surveyListSummary);
  const currentUser = useAppSelector(getCurrentUser);
  const limitSurveys = useAppSelector(FeaturePermissions.getLimitSurveys);

  const canManage = useMemo(() => {
    if (!currentUser) {
      return false;
    }
    if (surveyData.initiativeId) {
      return SurveyPermissions.canManage({ initiativeId: surveyData.initiativeId }, currentUser);
    }
    return InitiativePermissions.canManageInitiative(currentUser, initiativeId);
  }, [currentUser, surveyData, initiativeId]);

  const isLoaded = !isLoading && initiativeState.loaded && surveyListSummaryState.loaded && (!surveyId || surveyConfigLoaded);

  // Either we do not have surveyId, or we do, and it's the one we are have in the form;
  // Only then we consider form fully loaded with the right data
  const isFormFullyLoaded = !surveyId || (surveyId === form._id);

  const isEditMode = Boolean(currentSurveyConfig);
  const existingSurveys: Set<string> = useMemo(() => uniqueSurveyDatesBySurveyListSummary(surveyListSummaryState.data), [surveyListSummaryState]);

  const isCombinedReport = useMemo(() => surveyData?.type === SurveyType.Aggregation, [surveyData]);

  const hasChanged = useMemo(() => {
    if (form.name) {
      return true;
    }
    if (currentSurveyConfig?.period !== form.period) {
      return true;
    }
    if (currentSurveyConfig?.evidenceRequired !== form.evidenceRequired) {
      return true;
    }
    if (currentSurveyConfig?.noteRequired !== form.noteRequired) {
      return true;
    }
    if (currentSurveyConfig?.verificationRequired !== form.verificationRequired) {
      return true;
    }
    if (currentSurveyConfig?.isPrivate !== form.isPrivate) {
      return true;
    }
    if (getUtcDateUnit('year', currentSurveyConfig?.effectiveDate) !== form.year) {
      return true;
    }
    if (getUtcDateUnit('month', currentSurveyConfig?.effectiveDate) !== form.month) {
      return true;
    }
    if (currentSurveyConfig?.noteInstructions !== form.noteInstructions) {
      return true;
    }

    if (hasEditorStateChanged({
      current: currentSurveyConfig?.noteInstructionsEditorState,
      previous: form.noteInstructionsEditorState
    })) {
      return true;
    }

    if (!isSame(currentSurveyConfig?.deadlineDate, form.deadlineDate)) {
      return true;
    }
    if (!compareScheduledDates(currentSurveyConfig?.scheduledDates, form.scheduledDates)) {
      return true;
    }
    const unitFields = Object.keys(blueprintDefaultUnitConfig) as (keyof UnitConfig)[];
    if (unitConfig) {
      return unitFields.some(unitCode => unitConfig[unitCode] !== surveyData.unitConfig?.[unitCode]);
    }
    return false;
  }, [form, currentSurveyConfig, unitConfig, surveyData]);

  const isCompleted = Boolean(surveyData.completedDate);
  const isConfirmDisabled = isCompleted || !isLoaded || isConfirmBtnDisabled(form, isEditMode) || !!errorState || !hasChanged;

  useEffect(() => {
    if (initiativeId) {
      dispatch(loadCommon());
      dispatch(loadSurveyListSummaryByInitiativeId(initiativeId));
    }
  }, [dispatch, initiativeId]);

  useEffect(() => {
    if (surveyId) {
      dispatch(loadConfigById(surveyId))
    }
  }, [dispatch, surveyId])

  useEffect(() => {
    if (currentSurveyConfig) {
      const {
        evidenceRequired,
        noteRequired,
        verificationRequired,
        isPrivate,
        initiative,
        effectiveDate,
        period,
        sourceName,
        unitConfig,
        noteInstructions,
        deadlineDate,
        scheduledDates
      } = currentSurveyConfig;

      setForm((form) => ({
        ...form,
        _id: currentSurveyConfig._id,
        unitConfig,
        evidenceRequired,
        noteRequired,
        verificationRequired,
        isPrivate,
        initiative,
        sourceName,
        initiativeId: initiative?._id,
        period: period ?? DataPeriods.Yearly,
        year: getUtcDateUnit('year', effectiveDate),
        month: getUtcDateUnit('month', effectiveDate),
        noteInstructions,
        noteInstructionsEditorState: currentSurveyConfig.noteInstructionsEditorState,
        deadlineDate,
        scheduledDates
      }));

      setUnitConfig(unitConfig);
    }
  }, [currentSurveyConfig])

  useEffect(() => {
    if (!form.initiativeId) {
      return;
    }

    const isEditMode = currentSurveyConfig?._id;
    if (isEditMode) {
      // Don't care about survey limits for existing surveys
      if (errorState !== ERRORS.NO_ERROR) {
        setErrorState(ERRORS.NO_ERROR);
      }
      return;
    }

    if (FeaturePermissions.hasReachedLimit({ current: existingSurveys.size, limit: limitSurveys })) {
      setErrorState(ERRORS.REPORT_LIMIT);
      return;
    }

    if (errorState !== ERRORS.NO_ERROR) {
      setErrorState(ERRORS.NO_ERROR);
    }
  }, [limitSurveys, form, errorState, currentSurveyConfig, existingSurveys.size])

  useEffect(() => {
    const id = form.initiativeId;
    const hasConflict = hasSurveyConflict(
      surveyListSummaryState.data,
      {
        initiativeId: id,
        year: form.year,
        month: form.month,
        type: (surveyData.type ?? SurveyType.Default) as SurveyType,
        sourceName: form.sourceName,
        period: form.period,
      },
      surveyId
    );

    if (hasConflict) {
      setErrorState(ERRORS.REPORT_CONFLICT);
    } else if (errorState === ERRORS.REPORT_CONFLICT) {
      setErrorState(ERRORS.NO_ERROR);
    }
  }, [
    existingSurveys,
    form.initiativeId,
    form.year,
    form.month,
    form.period,
    form.sourceName,
    surveyListSummaryState.data,
    errorState,
    surveyId,
    surveyData.type
  ]);

  const { data: defaultSurveyConfig, isLoading: isLoadingSurveyConfig } = useGetDefaultSurveyConfigQuery({
    initiativeId,
  });

  useEffect(() => {
    if (isEditMode || isLoadingSurveyConfig || !defaultSurveyConfig) {
      return;
    }

    const { unitConfig, verificationRequired, isPrivate, evidenceRequired, noteRequired } = defaultSurveyConfig;
    setUnitConfig(unitConfig);
    setForm((prev) => ({ ...prev, verificationRequired, isPrivate, evidenceRequired, noteRequired }));
  }, [isEditMode, isLoadingSurveyConfig, defaultSurveyConfig]);

  const handleCreateSubmit = async () => {
    setState({ ...state, isLoading: true });
    const effectiveDate = createEffectiveDate(form) ?? new Date();
    const createdSurvey = await processSelfOnboarding({ ...form, effectiveDate, unitConfig });
    await analytics.track(AnalyticsEvents.SurveyCreated, {
      surveyId: createdSurvey._id,
      initiativeId: createdSurvey.initiativeId
    });
    dispatch(reloadSurveyList())
    dispatch(reloadSurveyListSummary());
    setState({ ...state, isLoading: false, message: 'Success' });

    if (createdSurvey._id) {
      history.push(
        generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
          initiativeId: createdSurvey.initiativeId,
          surveyId: createdSurvey._id,
          page: 'scope'
        })
      );
    }
  };

  const handleSubmit = () => {
    if (!currentSurveyConfig?._id) {
      handleCreateSubmit().catch(e => setError(e));
      return;
    }

    setState({ ...state, isLoading: true });
    const {
      evidenceRequired,
      noteRequired,
      verificationRequired,
      isPrivate,
      period,
      name,
      sourceName,
      noteInstructions,
      deadlineDate,
      scheduledDates,
    } = form;

    dispatch(updateConfigById(currentSurveyConfig._id, {
      name,
      period,
      evidenceRequired,
      noteRequired,
      verificationRequired,
      isPrivate,
      sourceName,
      effectiveDate: createEffectiveDate(form),
      unitConfig,
      noteInstructions,
      noteInstructionsEditorState: form.noteInstructionsEditorState,
      deadlineDate,
      scheduledDates
    })).then(() => {
      dispatch(reloadSurvey(currentSurveyConfig._id));
      dispatch(reloadSurveyListSummary()); // Need to reload, as it blocks view otherwise
      dispatch(reloadSurveyList());
      setState({ ...state, error: false, isLoading: false, message: 'Success' });
    }).catch((e: Error) => setError(e));
  };

  const setError = (e: Error) => setState({ ...state, error: true, isLoading: false, message: e.message });

  const isUserStaff = useAppSelector(isStaff);

  if (!canManage) {
    return isLoaded ? <NotAuthorised /> : <Loader />;
  }

  const goBack = () => {
    if (surveyId) {
      return history.push(generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId, page: 'overview' }));
    }
    return history.push(generateUrl(ROUTES.COMPANY_TRACKER_LIST, { initiativeId }));
  }

  const title = `Survey settings${isEditMode ? '' : ': Create new survey'}`;
  const additionalUnitConfig = {
    ...surveyData?.config?.unitConfig ?? {},
    ...surveyData?.unitConfig ?? {},
  };
  const disabledCurrencyOption = {
    currency: {
      disabled: true,
      canEnable: isUserStaff
    },
  };

  return (
    <>
      <SurveySettingsHeader />
      <DashboardSection icon='fal fa-cog' title={title}>
        <SurveySettingsMenu />
        <BasicAlert type={state.error || errored ? 'danger' : 'success'}>{errorMessage || message}</BasicAlert>
        {!isLoaded ? <Loader /> : <></>}
        <LoadingPlaceholder className='mt-4' count={1} height={800} isLoading={!isLoaded || !isFormFullyLoaded}>
          <Form className='survey-config-form'>
            {!isEditMode && (
              <BasicAlert hide={errorState !== ERRORS.REPORT_LIMIT} type={'warning'} className='mt-2'>
                {alertReportLimit}
              </BasicAlert>
            )}
            <BasicAlert hide={errorState !== ERRORS.REPORT_CONFLICT} type={'warning'}>
              {alertReportConflict}
            </BasicAlert>

            <SurveySettings updateForm={updateForm} form={form} errorState={errorState} />

            {isCombinedReport ? null : (
              <FormGroup className='mt-4' disabled={errorState === ERRORS.REPORT_LIMIT}>
                <SurveyUnitsAndCurrency
                  isCreating={!isEditMode}
                  unitConfig={unitConfig}
                  setUnitConfig={setUnitConfig}
                  isDisabled={Boolean(surveyData.completedDate) || disabled}
                  additionalUnitConfig={additionalUnitConfig}
                  warningText={
                    <>
                      <b>WARNING:</b> Updating default units will only affect unanswered {QUESTION.PLURAL}. Changing the
                      currency will update all {QUESTION.PLURAL} but any inputed financial values will not be converted.
                    </>
                  }
                  fieldOptions={disabledCurrencyOption}
                />
              </FormGroup>
            )}
            <NoteInstructionContainer
              canSetNoteInstructions={canSetNoteInstructions}
              editorId={surveyConfig?._id ?? 'new-config'}
              updateForm={updateForm}
              disabled={Boolean(surveyData.completedDate) || disabled}
              noteInstructionsEditorState={form.noteInstructionsEditorState}
              noteInstructions={form.noteInstructions}
            />
            <SurveyDeadlineReminder
              key={form.deadlineDate}
              deadlineDate={form.deadlineDate}
              scheduledDates={form.scheduledDates}
              updateForm={updateForm}
            />
            <div className='text-right'>
              <Button color='link-secondary' onClick={goBack} className='mr-3'>
                Cancel
              </Button>
              <Button color='primary' className='px-5' disabled={isConfirmDisabled} onClick={handleSubmit}>
                {isEditMode ? 'Update' : 'Create'}
              </Button>
            </div>
          </Form>
        </LoadingPlaceholder>
      </DashboardSection>
    </>
  );
}
